import { Link, useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Activity, Users, LogOut, User, UserCog, Shield, Monitor, Smartphone, ExternalLink, Code, Sparkles, ChevronDown, MapPin, Car, Menu } from 'lucide-react';
import { useClient, getIndustryRoles } from '@/contexts/ClientContext';
import { useAuth } from '@/contexts/AuthContext';
import AccountSwitcher from '@/components/AccountSwitcher';
import UpdatesNotification from '@/components/common/UpdatesNotification';
import NotificationsCenter from '@/components/notifications/NotificationsCenter';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { DEMO_CUSTOMERS } from '@/config/demoCustomers';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// Build demo options from DEMO_CUSTOMERS config
const DEMO_OPTIONS = Object.entries(DEMO_CUSTOMERS).map(([key, demo]) => ({
  id: key,
  name: demo.company,
  industry: demo.industry,
  subIndustry: demo.sub_industry,
  location: demo.location,
}));

export default function Header() {
  const { customerBusiness, setCustomerBusiness, setClientType, currentLocation, setCurrentLocation, availableLocations } = useClient();
  const { user, logout, isOwner} = useAuth();
  const navigate = useNavigate();
  
  const isMultiLocation = availableLocations.length > 0;
  
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };
  
  return (
    <header className="bg-background/80 backdrop-blur-md border-b border-border/50 transition-colors duration-300">
      <div className="w-full flex items-stretch gap-0" style={{ height: '72px' }}>
          {/* Logo column — fixed width on desktop so it sits flush above the sidebar nav */}
          <div className="flex items-center shrink-0 px-4 sm:px-6 lg:w-64 lg:px-3 lg:border-r lg:border-border/50">
          <Link to="/app" className="flex items-center gap-2 group shrink-0">
            <div className="relative overflow-hidden electric-dust">
              <img 
                src="/corvana-logo-320.webp" 
                alt="Corvana" 
                className="w-10 h-10 object-contain relative z-10"
              />
              <div className="particle particle-1"></div>
              <div className="particle particle-2"></div>
              <div className="particle particle-3"></div>
            </div>
            <h1 className="hidden sm:block text-lg sm:text-xl font-bold tracking-tight group-hover:text-primary transition-colors duration-300">
              Corvana™
            </h1>
          </Link>
          </div>

          {/* Content row — fills the remaining width next to the logo column */}
          <div className="flex-1 min-w-0 flex justify-between items-center gap-2 sm:gap-4 px-4 sm:px-6 lg:px-8">
          {/* Tenant Context Pill — anti-confusion indicator (always shows
              when user is logged in and on a demo/sandbox tenant). Keeps
              operators aware that they're not in a production tenant's data. */}
          {user?.is_demo && (
            <Badge
              variant="outline"
              data-testid="tenant-demo-indicator"
              className="hidden sm:flex items-center gap-1.5 border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 font-semibold px-2.5 py-1 text-[10px] uppercase tracking-wider rounded-full shrink-0"
              title={`Demo tenant${customerBusiness?.business_id ? ` · ${customerBusiness.business_id}` : ''}`}
            >
              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" />
              Demo
            </Badge>
          )}

          {/* Center: Demo Switcher - Owner only, DEMO accounts only.
              Real production clients must never see demo tooling. */}
          {(isOwner && user?.is_demo) ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="hidden md:flex items-center gap-2 border border-primary/30 text-primary font-medium px-3 py-1.5 text-xs rounded-full hover:bg-primary/5 transition-colors"
                  data-testid="demo-switcher-trigger"
                >
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                  {customerBusiness.name}
                  {customerBusiness?.business_id && (
                    <span
                      className="hidden xl:inline text-[10px] font-mono text-primary/60 ml-1"
                      data-testid="tenant-business-id"
                    >
                      {customerBusiness.business_id}
                    </span>
                  )}
                  <ChevronDown className="w-3 h-3 opacity-60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-64">
                <DropdownMenuLabel className="text-xs text-muted-foreground">Switch Demo Business</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {DEMO_OPTIONS.map((demo) => (
                  <DropdownMenuItem
                    key={demo.id}
                    data-testid={`demo-option-${demo.id}`}
                    className={`cursor-pointer ${customerBusiness?.name === demo.name ? 'bg-primary/10 font-semibold' : ''}`}
                    onClick={() => {
                      const newIndustry = demo.industry.charAt(0).toUpperCase() + demo.industry.slice(1);
                      const demoConfig = DEMO_CUSTOMERS[demo.id];
                      setCustomerBusiness(prev => ({
                        ...prev,
                        name: demo.name,
                        business_id: demoConfig?.business_id || demo.id,
                        industry: newIndustry,
                      }));
                      // Also set the owner role for this industry immediately
                      const roles = getIndustryRoles(demo.industry);
                      const ownerRole = roles[0]; // First role is always the owner
                      if (ownerRole) {
                        setClientType(ownerRole);
                        localStorage.setItem('corvana_selected_role', JSON.stringify(ownerRole));
                      }
                      localStorage.setItem('corvana_selected_industry', newIndustry);
                    }}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div>
                        <p className="text-sm font-medium">{demo.name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{demo.industry}</p>
                      </div>
                      {customerBusiness?.name === demo.name && (
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                      )}
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : user?.is_demo ? (
            <Badge 
              variant="outline" 
              data-testid="tenant-business-badge"
              className="hidden md:flex items-center gap-2 border-primary/30 text-primary font-medium px-3 py-1.5 text-xs rounded-full"
            >
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
              {customerBusiness.name}
              {customerBusiness?.business_id && (
                <span
                  className="hidden xl:inline text-[10px] font-mono text-primary/60 ml-1"
                  data-testid="tenant-business-id"
                >
                  {customerBusiness.business_id}
                </span>
              )}
            </Badge>
          ) : (
            <AccountSwitcher fallbackName={customerBusiness.name} />
          )}
          
          {/* Right Side */}
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            {/* Cory AI Trigger */}
            <Button 
              variant="outline" 
              size="sm" 
              data-testid="cory-header-trigger"
              className="hidden md:flex items-center gap-2 text-muted-foreground hover:text-foreground border-slate-300 dark:border-slate-600"
              onClick={() => {
                // Find and click the Cory chat bubble
                const bubble = document.querySelector('[data-testid="cory-chat-bubble"]');
                if (bubble) bubble.click();
              }}
            >
              <Sparkles className="w-4 h-4 text-emerald-500" />
              <span className="text-xs">Ask Cory</span>
            </Button>
            
            <a href="/" className="hidden sm:block">
              <Button variant="ghost" size="sm">
                Home
              </Button>
            </a>
            
            {/* Product updates — secondary, hidden on small screens to keep the bar tidy */}
            <span className="hidden sm:block">
              <UpdatesNotification />
            </span>
            
            {/* Theme Toggle */}
            <span className="hidden sm:block"><ThemeToggle size="sm" /></span>
            
            {/* Notifications Center */}
            <NotificationsCenter />

            {/* Mobile menu — exposes items hidden on small screens (one tap away, not lost) */}
            <div className="sm:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="px-2" data-testid="mobile-menu-button" aria-label="Open menu">
                    <Menu className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem
                    data-testid="mobile-menu-ask-cory"
                    onClick={() => { const b = document.querySelector('[data-testid="cory-chat-bubble"]'); if (b) b.click(); }}
                  >
                    <Sparkles className="w-4 h-4 mr-2 text-emerald-500" /> Ask Cory
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild data-testid="mobile-menu-home">
                    <a href="/"><span className="flex items-center"><ExternalLink className="w-4 h-4 mr-2" /> Home</span></a>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <div className="flex items-center justify-between px-2 py-1.5 text-sm" data-testid="mobile-menu-theme">
                    <span className="text-muted-foreground">Theme</span>
                    <ThemeToggle size="sm" />
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            {/* User Menu */}
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2" data-testid="user-menu-button">
                    <div className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center">
                      <User className="w-3.5 h-3.5 text-primary" />
                    </div>
                    <span className="hidden sm:inline text-sm">{user.first_name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span>{user.first_name} {user.last_name}</span>
                      <span className="text-xs text-muted-foreground font-normal">{user.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/app/profile')}>
                    <User className="w-4 h-4 mr-2" />
                    Profile Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/app/security')}>
                    <Shield className="w-4 h-4 mr-2" />
                    Security
                  </DropdownMenuItem>
                  {isOwner() && (
                    <DropdownMenuItem onClick={() => navigate('/app/user-management')}>
                      <UserCog className="w-4 h-4 mr-2" />
                      User Management
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          </div>
        </div>
        
        {/* Multi-Location Selector Bar (QSR) */}
        {isMultiLocation && isOwner && (
          <div className="border-t border-border/30 px-4 sm:px-6 lg:px-8 py-1.5 flex items-center gap-2 overflow-x-auto scrollbar-hide" data-testid="location-selector-bar">
            <MapPin className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <button
              data-testid="location-all"
              onClick={() => setCurrentLocation(null)}
              className={`shrink-0 px-3 py-1 text-xs font-medium rounded-full transition-all duration-200 ${
                !currentLocation
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              All Locations ({availableLocations.length})
            </button>
            <div className="w-px h-4 bg-border/50 shrink-0" />
            {availableLocations.map((loc) => (
              <button
                key={loc.id}
                data-testid={`location-${loc.id}`}
                onClick={() => setCurrentLocation(loc)}
                className={`shrink-0 px-3 py-1 text-xs font-medium rounded-full transition-all duration-200 flex items-center gap-1.5 ${
                  currentLocation?.id === loc.id
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {loc.type === 'drive_through' && <Car className="w-3 h-3" />}
                {loc.shortName}
                <span className="text-[10px] opacity-70">{loc.state}</span>
              </button>
            ))}
          </div>
        )}
      </header>
  );
}
